import { useState } from 'react';
import { useNavigate } from 'react-router';
import type { ApiError, EventDetail } from '../../api';
import { eventEndpoints } from '../../api';
import { Button, Field, Panel } from '../../ui';
import { INPUT, zonedToUtc } from './shared';
import { dateWords } from '../../terms';

/**
 * Copy an event (D-164).
 *
 * Next year's Nutcracker is this year's Nutcracker with new dates, and retyping
 * the zones, ticket types, fees, taxes, delivery methods and revenue split is
 * both a morning's work and the place a mistake hides until settlement. The copy
 * carries the configuration only: it arrives as a draft, with a draft price
 * table, so it cannot be sold from until somebody has looked at the prices.
 *
 * The dates are optional. An operator who wants them added here can paste them;
 * one who would rather build the run on the Performances tab leaves the box
 * empty and gets an event with no dates at all.
 */

const slugify = (title: string): string =>
  title
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 120);

export function DuplicateCard({ detail, onError, onClose }: { detail: EventDetail; onError: (error: ApiError) => void; onClose: () => void }) {
  const navigate = useNavigate();
  const nextYear = String(new Date().getUTCFullYear() + 1);
  const [title, setTitle] = useState(`${detail.event.title} ${nextYear}`);
  const [slug, setSlug] = useState(slugify(`${detail.event.title} ${nextYear}`));
  const [slugEdited, setSlugEdited] = useState(false);
  const [dates, setDates] = useState('');
  const [busy, setBusy] = useState(false);

  // The clock the pasted dates are read in. A copy plays the same room as the
  // original, so its performances keep the original's timezone.
  const timezone = detail.performances[0]?.timezone ?? 'America/Chicago';

  const setTitleAndSlug = (value: string) => {
    setTitle(value);
    if (!slugEdited) setSlug(slugify(value));
  };

  const submit = async () => {
    setBusy(true);
    try {
      const instants: string[] = [];
      for (const wall of dates.split('\n').map((line) => line.trim()).filter(Boolean)) {
        // `zonedToUtc` wants the datetime-local shape, so a pasted space is
        // read as the T it stands for rather than refused on a technicality.
        const instant = zonedToUtc(wall.replace(' ', 'T'), timezone);
        if (!instant) throw Object.assign(new Error(`"${wall}" is not a date and time — write it as 2027-12-04 19:30`), { code: 'BAD_DATE' });
        instants.push(instant);
      }
      const result = await eventEndpoints.duplicate(detail.event.id, { slug, title, ...(instants.length > 0 ? { performance_dates: instants } : {}) });
      navigate(`/events/${result.event.id}`);
    } catch (cause) {
      onError(cause as ApiError);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel
      title="Copy this event"
      description={`Zones, ticket types, prices, fees, taxes, delivery, exchange rules, accessibility, sponsors, categories, the page and the revenue split. Never ${dateWords().many}, orders, tickets or anything the ledger wrote.`}
      actions={<Button onClick={onClose}>Cancel</Button>}
    >
      <div className="space-y-4 px-5 py-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Title" htmlFor="copy-title">
            <input id="copy-title" className={`${INPUT} w-full`} value={title} onChange={(event) => setTitleAndSlug(event.target.value)} />
          </Field>
          <Field label="Web address" htmlFor="copy-slug" hint="The storefront address: /events/…">
            <input
              id="copy-slug"
              className={`${INPUT} w-full`}
              value={slug}
              onChange={(event) => {
                setSlugEdited(true);
                setSlug(event.target.value);
              }}
            />
          </Field>
        </div>
        <Field label="Dates (optional)" htmlFor="copy-dates" hint={`One per line, as 2027-12-04 19:30, in ${timezone.replaceAll('_', ' ')}. They arrive as drafts with no seats until you build the inventory.`}>
          <textarea
            id="copy-dates"
            className={`${INPUT} min-h-24 w-full font-mono`}
            value={dates}
            onChange={(event) => setDates(event.target.value)}
            placeholder={'2027-12-04 19:30\n2027-12-05 14:00'}
          />
        </Field>
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="primary" onClick={() => void submit()} disabled={busy || !title.trim() || !slug.trim()}>
            {busy ? 'Copying…' : 'Create the copy'}
          </Button>
          <span className="text-xs text-ink-400">The copy opens as a draft, with its prices in a draft table.</span>
        </div>
      </div>
    </Panel>
  );
}
